/**
 * Global state machine for the checkout modal. Introduced by
 * subscription-tiers-rewrite Group 1; corrected for the BE-shipped 4-tier
 * contract in Group 0.5; rewritten by fix-checkout-stale-payment-intent-rehydration
 * to mirror main's PI-lifecycle invariants.
 *
 * Key invariants restored from main (CheckoutPage.js, deleted in Group 10):
 *   - `paymentIntent` field stores the FULL Chargebee `payment_intent` object —
 *     NOT just the id. The Chargebee SDK's `authorizeWith3ds` reads
 *     `gateway_account_id`, `amount`, `currency_code`, `active_payment_attempt`
 *     off the input object; passing `{ id }` alone triggers VALIDATION_FAILED.
 *   - `paymentId` is mirrored to the URL for refresh-resilience.
 *   - The full PI is also persisted to localStorage (keyed by `payment_id`) so
 *     `recoverCheckoutIntent` can restore the journey on refresh WITHOUT
 *     calling `refresh-payment-intent` (the BE rejects that endpoint when
 *     called with only `{ payment_id }`).
 *   - There is NO `'stale'` IntentStatus. Stale-intent failures during submit
 *     surface as an inline error + retry button in the slide. Page-refresh
 *     recovery uses `checkDirectPaymentStatus` + localStorage cache.
 *
 * Bug-B fix: on `already_subscribed` 409 from `get-checkout-url`, the thunk
 * dispatches `close()` instead of `setIntentStatus('error')`. The existing
 * chatSlice toast + `getUserMetadata` refresh handle the user-facing UX.
 */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import {
  persistPaymentIntent,
  readPersistedPaymentIntent,
  readPersistedGateway,
  clearPersistedPaymentIntent,
  clearConsultationDescription,
} from "src/utils/checkoutPersistence";
import { isAlreadySubscribedDetail } from "src/utils/paymentErrors";
import { trackAnalytics } from "src/pages/helpers";
import { PLANS_V3 } from "src/utils/Constants";
import { Plan } from "src/domain/plan";
import { EVENT_NAMES, EVENT_PARAMETERS } from "src/utils/EventConstants";
import type { GatewayKey } from "src/utils/paymentSourceEligibility";

export type CheckoutEntry =
  | "message_limit"
  | "message_warning"
  | "thread_limit"
  | "locked_doc"
  | "consultation"
  | "upgrade_for_tasks"
  | "pricing_nav"
  | "privilege_upgrade"
  | "attachment_limit"
  | "billing_change_plan"
  // "Get started" CTAs on the marketing website / landing page.
  | "get_started";

export const CHECKOUT_ENTRY = {
  MESSAGE_LIMIT: "message_limit",
  MESSAGE_WARNING: "message_warning",
  THREAD_LIMIT: "thread_limit",
  LOCKED_DOC: "locked_doc",
  CONSULTATION: "consultation",
  UPGRADE_FOR_TASKS: "upgrade_for_tasks",
  PRICING_NAV: "pricing_nav",
  PRIVILEGE_UPGRADE: "privilege_upgrade",
  ATTACHMENT_LIMIT: "attachment_limit",
  BILLING_CHANGE_PLAN: "billing_change_plan",
  GET_STARTED: "get_started",
} as const satisfies Record<string, CheckoutEntry>;

/** sessionStorage key: checkout entry stashed by Signup/Signin, read once by PostAuth/Home if the URL lost it. */
export const CHECKOUT_ENTRY_STORAGE_KEY = "checkout_entry";

export type CheckoutStep = "plan_picker" | "checkout" | "success";

/**
 * Wire-value tier discriminator. Matches BE `subscription_type` exactly so
 * there is no FE↔BE mapping layer to drift.
 */
export type CheckoutTier = "subscriber_business" | "subscriber_enterprise" | "inhouse_counsel";

export type IntentStatus = "idle" | "loading" | "ready" | "error";

/**
 * Purchase-target discriminator, added by `additional-consultation-one-time-charge`.
 * `selectedTier` remains the field every existing subscription-flow reader
 * uses; `purchaseTarget` is the superset that also covers one-time charges.
 * The two are kept in sync by the reducers below: `selectedTier` mirrors
 * `purchaseTarget.tier` for a subscription target and is `null` for a
 * one-time target.
 */
export type PurchaseTarget =
  | { kind: "subscription"; tier: CheckoutTier }
  | {
      kind: "one_time";
      transactionType: "legal_consultation";
      /** Optional diagnostic-only id of the QuickActionButtonsMessage that initiated the purchase. Never validated, never blocking. */
      messageId?: string | null;
    };

/**
 * Shape of the Chargebee `payment_intent` object as returned by
 * `get-checkout-url` / `refresh-payment-intent`. The fields enumerated below
 * are the ones the Chargebee SDK reads internally (documented for `gateway`,
 * `amount`, `currency_code`, `active_payment_attempt`; inferred for the rest).
 * The index signature `[key: string]: unknown` preserves any additional fields
 * the BE returns so the object survives a round-trip unchanged.
 */
export interface ChargebeePaymentIntent {
  id: string;
  status: string;
  amount: number;
  currency_code: string;
  gateway_account_id: string;
  expires_at?: number;
  payment_method_type?: string;
  active_payment_attempt?: {
    id?: string;
    id_at_gateway?: string;
    status?: string;
    error_code?: string | null;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface CheckoutFlowState {
  open: boolean;
  entry: CheckoutEntry | null;
  step: CheckoutStep | null;
  selectedTier: CheckoutTier | null;
  /** Superset of `selectedTier` — also covers one-time purchase targets. See `PurchaseTarget`. */
  purchaseTarget: PurchaseTarget | null;
  threadId: string | null;
  additionalInfo: string;
  /** Chargebee `payment_id` mirrored to URL (?payment_id=…). */
  paymentId: string | null;
  /** Full Chargebee `payment_intent` object. NOT mirrored to URL. */
  paymentIntent: ChargebeePaymentIntent | null;
  /**
   * Billing entity collecting this purchase — the TOP-LEVEL `gateway` on the
   * checkout response, a sibling of `payment_intent`. Not to be confused with
   * `paymentIntent.gateway`, which is Chargebee's own field ("stripe").
   *
   * Drives saved-card eligibility. `null` is normalised to `"inc"` by
   * `normalizeGateway` at the point of comparison, never here — the slice
   * stores what the BE said.
   */
  gateway: GatewayKey | null;
  intentStatus: IntentStatus;
  /**
   * True when `recoverCheckoutIntent` auto-minted a fresh PI to replace an
   * expired/failed/cache-missed one. Drives the soft inline banner in the
   * slide. Cleared by `close()`, `setSelectedTier`, and the explicit
   * `dismissStaleRecoveredBanner` action.
   */
  staleRecovered: boolean;
  /** doc_id carried from the consultation shelf so SuccessSlide can include it in the referral payload. */
  consultationDocId: string | null;
  /** referral_category carried from the consultation shelf button context. */
  consultationReferralCategory: string;
  /** Shelf headline carried so checkout can display the same dynamic copy. */
  consultationSubheader: string | null;
  /** Document title carried from locked-doc entry so checkout can display it. */
  lockedDocTitle: string | null;
}

export interface OpenAtPayload {
  entry: CheckoutEntry;
  step: CheckoutStep;
  selectedTier?: CheckoutTier | null;
  /** One-time purchase target. Mutually exclusive with `selectedTier` — pass one or the other, never both. */
  purchaseTarget?: PurchaseTarget | null;
  threadId?: string | null;
  /**
   * Consultation description carried into the flow atomically. openAt RESETS
   * `additionalInfo` when this is absent, so a separate `setAdditionalInfo`
   * dispatched BEFORE openAt is silently wiped — the shelf hit exactly that
   * (consultation-contract-rev3 erratum). Callers with a description MUST
   * pass it here, not as a preceding dispatch.
   */
  additionalInfo?: string;
  consultationDocId?: string | null;
  consultationReferralCategory?: string;
  consultationSubheader?: string | null;
  lockedDocTitle?: string | null;
}

const initialState: CheckoutFlowState = {
  open: false,
  entry: null,
  step: null,
  selectedTier: null,
  purchaseTarget: null,
  threadId: null,
  additionalInfo: "",
  paymentId: null,
  paymentIntent: null,
  gateway: null,
  intentStatus: "idle",
  staleRecovered: false,
  consultationDocId: null,
  consultationReferralCategory: "business",
  consultationSubheader: null,
  lockedDocTitle: null,
};

const checkoutFlowSlice = createSlice({
  name: "checkoutFlow",
  initialState,
  reducers: {
    openAt: (state, action: PayloadAction<OpenAtPayload>) => {
      const { entry, step, selectedTier, purchaseTarget, threadId, consultationDocId, consultationReferralCategory, consultationSubheader, lockedDocTitle } =
        action.payload;
      state.open = true;
      state.entry = entry;
      state.step = step;
      if (purchaseTarget) {
        state.purchaseTarget = purchaseTarget;
        state.selectedTier = purchaseTarget.kind === "subscription" ? purchaseTarget.tier : null;
      } else {
        state.selectedTier = selectedTier ?? null;
        state.purchaseTarget = state.selectedTier ? { kind: "subscription", tier: state.selectedTier } : null;
      }
      state.threadId = threadId ?? null;
      state.additionalInfo = action.payload.additionalInfo ?? "";
      state.paymentId = null;
      state.paymentIntent = null;
      state.gateway = null;
      state.intentStatus = "idle";
      state.staleRecovered = false;
      state.consultationDocId = consultationDocId ?? null;
      state.consultationReferralCategory = consultationReferralCategory ?? "business";
      state.consultationSubheader = consultationSubheader ?? null;
      state.lockedDocTitle = lockedDocTitle ?? null;
    },
    setStep: (state, action: PayloadAction<CheckoutStep>) => {
      state.step = action.payload;
    },
    /**
     * Tier-switch invalidates any in-flight PI. The localStorage cache for the
     * old PI is cleared by the `switchTier` thunk before this reducer fires.
     */
    setSelectedTier: (state, action: PayloadAction<CheckoutTier>) => {
      if (state.selectedTier !== action.payload) {
        state.paymentId = null;
        state.paymentIntent = null;
        state.gateway = null;
        state.intentStatus = "idle";
        state.staleRecovered = false;
      }
      state.selectedTier = action.payload;
      state.purchaseTarget = { kind: "subscription", tier: action.payload };
    },
    /**
     * Switch to a one-time purchase target mid-flow (mirrors `setSelectedTier`
     * for the one-time case). Invalidates any in-flight PI the same way.
     */
    setPurchaseTarget: (state, action: PayloadAction<PurchaseTarget>) => {
      const changed =
        !state.purchaseTarget ||
        state.purchaseTarget.kind !== action.payload.kind ||
        (action.payload.kind === "subscription" &&
          state.purchaseTarget.kind === "subscription" &&
          state.purchaseTarget.tier !== action.payload.tier) ||
        (action.payload.kind === "one_time" &&
          state.purchaseTarget.kind === "one_time" &&
          state.purchaseTarget.transactionType !== action.payload.transactionType);
      if (changed) {
        state.paymentId = null;
        state.paymentIntent = null;
        state.gateway = null;
        state.intentStatus = "idle";
        state.staleRecovered = false;
      }
      state.purchaseTarget = action.payload;
      state.selectedTier = action.payload.kind === "subscription" ? action.payload.tier : null;
    },
    setAdditionalInfo: (state, action: PayloadAction<string>) => {
      state.additionalInfo = action.payload;
    },
    /**
     * Set the full PI pair on the slice.
     *
     * Invariant: `intentStatus === 'ready'` implies BOTH `paymentId !== null`
     * AND `paymentIntent !== null`. Violation coerces `intentStatus` to
     * `'loading'` so the mid-fetch caller can finish.
     */
    setPaymentIntent: (
      state,
      action: PayloadAction<{
        paymentId: string | null;
        paymentIntent: ChargebeePaymentIntent | null;
        gateway?: GatewayKey | null;
        intentStatus: IntentStatus;
      }>,
    ) => {
      const { paymentId, paymentIntent, gateway, intentStatus } = action.payload;
      state.paymentId = paymentId;
      state.paymentIntent = paymentIntent;
      state.gateway = gateway ?? null;
      state.intentStatus = intentStatus === "ready" && (!paymentId || !paymentIntent) ? "loading" : intentStatus;
    },
    /**
     * Refresh-in-place: update `paymentIntent` only. Leaves `paymentId` and
     * `intentStatus` untouched. Used for coupon / saved-card refresh flows so
     * the card form never unmounts.
     */
    setPaymentIntentObject: (state, action: PayloadAction<ChargebeePaymentIntent | null>) => {
      state.paymentIntent = action.payload;
    },
    /**
     * Refresh-in-place for the gateway label. `refresh-payment-intent` and any
     * other response that mints a fresh intent may return a different entity
     * than the one we last filtered against, so the newest response wins.
     */
    setGateway: (state, action: PayloadAction<GatewayKey | null>) => {
      state.gateway = action.payload;
    },
    setIntentStatus: (state, action: PayloadAction<IntentStatus>) => {
      state.intentStatus = action.payload;
    },
    setStaleRecovered: (state, action: PayloadAction<boolean>) => {
      state.staleRecovered = action.payload;
    },
    dismissStaleRecoveredBanner: (state) => {
      state.staleRecovered = false;
    },
    close: () => initialState,
  },
});

export const {
  openAt,
  setStep,
  setSelectedTier,
  setPurchaseTarget,
  setAdditionalInfo,
  setPaymentIntent,
  setPaymentIntentObject,
  setGateway,
  setIntentStatus,
  setStaleRecovered,
  dismissStaleRecoveredBanner,
  close,
} = checkoutFlowSlice.actions;

// ─── Selectors ───────────────────────────────────────────────────────────────

/** Derived: the PI's id alone, for callers that don't need the full object. */
export const selectPaymentIntentId = (state: { checkoutFlow: CheckoutFlowState }): string | null =>
  state.checkoutFlow.paymentIntent?.id ?? null;

/** Derived: the active one-time target, or null if the current target is a subscription (or none). */
export const selectOneTimeTarget = (state: {
  checkoutFlow: CheckoutFlowState;
}): (PurchaseTarget & { kind: "one_time" }) | null => {
  const target = state.checkoutFlow.purchaseTarget;
  return target?.kind === "one_time" ? target : null;
};

// ─── Thunk plumbing ──────────────────────────────────────────────────────────

type ThunkDispatch = (action: unknown) => unknown;
type ThunkGetState = () => { checkoutFlow: CheckoutFlowState };

interface FullUserMetadata {
  subscription_type?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  phone_number?: string | null;
  user_id?: string | null;
  individual_info?: { state_of_residence?: string | null } | null;
  [key: string]: unknown;
}

interface MinimalGetState {
  (): {
    onboarding?: {
      userMetadata?: FullUserMetadata | null;
    };
    homeslice?: {
      email?: string | null;
    };
    checkoutFlow?: CheckoutFlowState;
  };
}

type Dispatch = (action: unknown) => unknown;

type ChatSliceModule = {
  getCheckoutUrl: (payload: unknown) => unknown;
  checkDirectPaymentStatus: (payload: unknown) => unknown;
};

// eslint-disable-next-line @typescript-eslint/no-var-requires, @typescript-eslint/no-require-imports
const getChatSlice = (): ChatSliceModule =>
  // @ts-ignore -- lazy require to break circular dependency
  require("src/store/slices/chatSlice");

// ─── Thunk: openWithEntry (unchanged from prior baseline) ────────────────────

export interface OpenWithEntryArg {
  entry: CheckoutEntry;
  threadId?: string | null;
  initialStep?: CheckoutStep;
  selectedTier?: CheckoutTier;
  /** One-time purchase target. When present, this takes priority over all tier-resolution logic below and lands directly on Checkout. */
  purchaseTarget?: PurchaseTarget;
  /** Shelf description — threaded through openAt so it survives the reset there. See OpenAtPayload.additionalInfo. */
  additionalInfo?: string;
  consultationDocId?: string | null;
  consultationReferralCategory?: string;
  consultationSubheader?: string | null;
  lockedDocTitle?: string | null;
}

const resolveOpenTarget = (
  subscriptionType: string | null | undefined,
): { step: CheckoutStep; selectedTier: CheckoutTier | null } => {
  const plan = Plan.from(subscriptionType);
  // The $49 AI plan is the only tier that skips the picker and jumps straight
  // to Counsel checkout — it is the one plan with a single, obvious upgrade.
  if (plan.isRank(1)) {
    return { step: "checkout", selectedTier: "subscriber_enterprise" };
  }
  // Everyone else lands on plan_picker with no pre-selected tier. For paid
  // plans with no upgrade path (Counsel/Plus — `Plan.hasUpgradePath()` is the
  // single home of the INH-5059 rule) the orchestrator's
  // `isAlreadySubscribedNoUpgrade` guard shows the "already subscribed" modal
  // for non-pricing entries.
  return { step: "plan_picker", selectedTier: null };
};

export const openWithEntry =
  (arg: OpenWithEntryArg) =>
  (dispatch: Dispatch, getState: MinimalGetState): void => {
    // One-time purchase targets bypass all tier-resolution logic below — they
    // are never picker-eligible and always land directly on Checkout.
    if (arg.purchaseTarget?.kind === "one_time") {
      dispatch(
        openAt({
          entry: arg.entry,
          step: "checkout",
          purchaseTarget: arg.purchaseTarget,
          threadId: arg.threadId ?? null,
          additionalInfo: arg.additionalInfo,
          consultationDocId: arg.consultationDocId ?? null,
          consultationReferralCategory: arg.consultationReferralCategory,
          consultationSubheader: arg.consultationSubheader ?? null,
          lockedDocTitle: arg.lockedDocTitle ?? null,
        }),
      );
      const state = getState();
      dispatch(
        prepareCheckoutIntent({
          target: arg.purchaseTarget,
          threadId: arg.threadId ?? null,
          entry: arg.entry,
          additionalInfo: arg.additionalInfo ?? "",
          consultationDocId: arg.consultationDocId ?? null,
          consultationReferralCategory: arg.consultationReferralCategory,
          // Read at dispatch time (not later) so the credits-race fallback has
          // the metadata/email it needs without re-reading state mid-async-flow.
          userMetadata: state?.onboarding?.userMetadata ?? null,
          userEmail: state?.homeslice?.email ?? null,
        }) as unknown as { type: string },
      );
      return;
    }

    const state = getState();
    const subscriptionType = state?.onboarding?.userMetadata?.subscription_type;
    const resolved = resolveOpenTarget(subscriptionType);

    // `pricing_nav` (sidenav "Upgrade plan") and `billing_change_plan`
    // (BillingTab "Change plan") must always land on the 3-card picker —
    // paid tiers should see all plans with their current one marked, not
    // get skipped straight to the next-tier Checkout slide.
    const isAllPlansEntry = arg.entry === "pricing_nav" || arg.entry === "billing_change_plan";
    const resolvedStep: CheckoutStep = isAllPlansEntry ? "plan_picker" : resolved.step;
    const resolvedSelectedTier: CheckoutTier | null = isAllPlansEntry ? null : resolved.selectedTier;

    // Per task 11.E.8: consultation-entry from Free / pre_onboarding skips the
    // picker entirely and lands directly on Counsel checkout. Caller-supplied
    // overrides still win, so the Counsel-exhausted matrix row (which passes
    // `selectedTier: 'inhouse_counsel'` explicitly) is unaffected.
    const isFreeConsultationDirect =
      arg.entry === "consultation" && arg.selectedTier === undefined && Plan.from(subscriptionType).isFree();

    // locked_doc entry from Free / pre_onboarding skips the
    // multi-plan picker and lands directly on the $49 Pro checkout.
    const isFreeLockedDocDirect =
      arg.entry === "locked_doc" && arg.selectedTier === undefined && Plan.from(subscriptionType).isFree();

    const autoFilledStep: CheckoutStep | null = isFreeConsultationDirect || isFreeLockedDocDirect ? "checkout" : null;
    const autoFilledSelectedTier: CheckoutTier | null = isFreeConsultationDirect
      ? (PLANS_V3.INHOUSE_COUNSEL as CheckoutTier)
      : isFreeLockedDocDirect
        ? (PLANS_V3.INHOUSE_AI as CheckoutTier)
        : null;

    // Explicit overrides win over computed defaults — the HTTP/WS safety nets
    // sometimes need to force a specific entry path regardless of tier.
    const step = arg.initialStep ?? autoFilledStep ?? resolvedStep;
    const selectedTier =
      arg.selectedTier !== undefined ? arg.selectedTier : (autoFilledSelectedTier ?? resolvedSelectedTier);

    dispatch(
      openAt({
        entry: arg.entry,
        step,
        selectedTier: selectedTier ?? null,
        threadId: arg.threadId ?? null,
        additionalInfo: arg.additionalInfo,
        consultationDocId: arg.consultationDocId ?? null,
        consultationReferralCategory: arg.consultationReferralCategory,
        consultationSubheader: arg.consultationSubheader ?? null,
        lockedDocTitle: arg.lockedDocTitle ?? null,
      }),
    );

    if (step === "checkout" && selectedTier) {
      dispatch(
        prepareCheckoutIntent({
          tier: selectedTier,
          threadId: arg.threadId ?? null,
          entry: arg.entry,
        }) as unknown as { type: string },
      );
    }
  };

// ─── Thunk: prepareCheckoutIntent ────────────────────────────────────────────

export type PaymentFrequency = "monthly";

export interface PrepareCheckoutIntentArg {
  /** Legacy shape — a bare subscription tier. Prefer `target` for new call sites. */
  tier?: CheckoutTier;
  /** Preferred shape — covers both subscription and one-time targets. Takes priority over `tier` when both are present. */
  target?: PurchaseTarget;
  threadId?: string | null;
  entry?: CheckoutEntry;
  /** Diagnostic-only id of the originating QuickActionButtonsMessage. Never validated, never blocking. One-time targets only. */
  messageId?: string | null;
  /**
   * The following four fields exist ONLY to support the
   * `consultation_credits_available` (409) fallback below: if the BE finds
   * the user already holds a usable credit, this thunk redirects straight to
   * the credit-backed referral-submit path instead of showing a payment
   * error. They are passed explicitly by the caller (rather than read via
   * `getState`) so the fallback payload doesn't depend on a wider state shape
   * than this thunk otherwise needs.
   */
  additionalInfo?: string;
  consultationDocId?: string | null;
  consultationReferralCategory?: string;
  userMetadata?: FullUserMetadata | null;
  userEmail?: string | null;
}

interface CheckoutUrlResponse {
  payment_id?: string;
  payment_intent?: ChargebeePaymentIntent | null;
  /**
   * Billing entity collecting this purchase. TOP-LEVEL sibling of
   * `payment_intent` — NOT `payment_intent.gateway`, which is Chargebee's own
   * field ("stripe") and would match no card.
   */
  gateway?: GatewayKey | null;
  [key: string]: unknown;
}

interface CheckoutUrlResult {
  response: CheckoutUrlResponse;
}

// This slice sees the error as the { error: axiosError } payload that
// rejectWithValue produces (surfaced via `.unwrap()`), so normalize down to
// `detail` before applying the shared match rule. Keeping the rule in
// src/utils/paymentErrors — instead of importing chatSlice's predicate — also
// avoids a slice-to-slice import, matching the lazy getChatSlice() pattern this
// file already uses to dodge a circular dependency. (Per PR #1395 review.)
export const isAlreadySubscribedError = (err: unknown): boolean =>
  isAlreadySubscribedDetail(
    (err as { error?: { response?: { data?: { detail?: unknown } } } })?.error?.response?.data?.detail,
  );

/**
 * True when the BE rejects with 409 `consultation_credits_available` — the
 * user actually holds a usable consultation credit (a race: it arrived
 * between the shelf submit and this request settling). This is NOT a payment
 * failure; see the catch branch in `prepareCheckoutIntent` below.
 */
export const isConsultationCreditsAvailableError = (err: unknown): boolean => {
  const detail = (err as { error?: { response?: { data?: { detail?: { error_code?: string } } } } })?.error?.response
    ?.data?.detail;
  return detail?.error_code === "consultation_credits_available";
};

type OnboardingSliceModule = {
  storeReferral: (payload: Record<string, unknown>) => unknown;
};

// eslint-disable-next-line @typescript-eslint/no-var-requires, @typescript-eslint/no-require-imports
const getOnboardingSlice = (): OnboardingSliceModule =>
  // @ts-ignore -- lazy require to break circular dependency
  require("src/store/slices/onboardingSlice");

export const prepareCheckoutIntent =
  (arg: PrepareCheckoutIntentArg) =>
  async (dispatch: ThunkDispatch, getState?: ThunkGetState): Promise<void> => {
    if (getState && getState().checkoutFlow?.intentStatus === "loading") {
      // Dedupe: another fetch is in flight (e.g. StrictMode double-fire).
      return;
    }

    const target: PurchaseTarget | null = arg.target ?? (arg.tier ? { kind: "subscription", tier: arg.tier } : null);

    if (!target) {
      dispatch(setIntentStatus("error"));
      return;
    }

    if (target.kind === "one_time" && !arg.threadId) {
      // eslint-disable-next-line no-console
      console.error("[checkout] missing thread context for one-time purchase target", target);
      dispatch(setIntentStatus("error"));
      return;
    }

    dispatch(setIntentStatus("loading"));
    try {
      const { getCheckoutUrl } = getChatSlice();
      const action =
        target.kind === "subscription"
          ? getCheckoutUrl({
              payment_type: "subscription",
              subscription_info: {
                subscription_type: target.tier,
                payment_frequency: "monthly" as PaymentFrequency,
              },
              ...(arg.threadId ? { legal_review_thread_id: arg.threadId } : {}),
            })
          : getCheckoutUrl({
              payment_type: "transaction",
              transaction_type: target.transactionType,
              legal_review_thread_id: arg.threadId,
              ...(arg.messageId ? { message_id: arg.messageId } : {}),
            });
      const promise = dispatch(action) as { unwrap: () => Promise<CheckoutUrlResult> };
      const result = await promise.unwrap();
      const paymentId = result?.response?.payment_id ?? null;
      const paymentIntent = result?.response?.payment_intent ?? null;
      const gateway = result?.response?.gateway ?? null;

      const missingField =
        !paymentId ||
        !paymentIntent ||
        !paymentIntent.id ||
        typeof paymentIntent.amount !== "number" ||
        !paymentIntent.currency_code ||
        !paymentIntent.gateway_account_id;

      if (missingField) {
        // eslint-disable-next-line no-console
        console.error("[checkout] get-checkout-url returned an incomplete intent", {
          hasPaymentId: !!paymentId,
          hasPaymentIntent: !!paymentIntent,
          hasIntentId: !!paymentIntent?.id,
          hasAmount: typeof paymentIntent?.amount === "number",
          hasCurrencyCode: !!paymentIntent?.currency_code,
          hasGatewayAccountId: !!paymentIntent?.gateway_account_id,
        });
        dispatch(setIntentStatus("error"));
        return;
      }

      // Persist to localStorage so a page refresh can recover without calling
      // refresh-payment-intent (the BE rejects that endpoint when called with
      // only { payment_id }).
      persistPaymentIntent(paymentId as string, paymentIntent, gateway);

      dispatch(
        setPaymentIntent({
          paymentId,
          paymentIntent,
          gateway,
          intentStatus: "ready",
        }),
      );
    } catch (err) {
      // Bug-B: an already-subscribed user re-opening the modal should NOT see
      // the generic "couldn't prepare checkout" error. The chatSlice handler
      // (getCheckoutUrl.rejected) already fires a toast + getUserMetadata; we
      // just close the modal here.
      if (isAlreadySubscribedError(err)) {
        dispatch(close());
        return;
      }

      // Credits arrived between the shelf submit and this request settling
      // (monthly refresh, admin grant, or a concurrent purchase settling
      // first). The user already holds what they were about to buy — close
      // the checkout (never show it) and route through the existing
      // credit-backed referral-submit path instead of a payment error.
      // additional-consultation-purchase spec: "consultation_credits_available
      // is a routing signal, not a payment failure".
      if (isConsultationCreditsAvailableError(err)) {
        dispatch(close());
        trackAnalytics(EVENT_NAMES.ADDITIONAL_CONSULTATION_CREDIT_RACE, {
          [EVENT_PARAMETERS.THREAD_ID]: arg.threadId ?? undefined,
          stage: "checkout",
        });
        const { storeReferral } = getOnboardingSlice();
        const meta = arg.userMetadata ?? null;
        const fullName = `${meta?.first_name ?? ""} ${meta?.last_name ?? ""}`.trim();
        const phoneDigits = String(meta?.phone_number ?? "").replace(/^\+1/, "");
        dispatch(
          storeReferral({
            name: fullName,
            email: arg.userEmail ?? "",
            phone: phoneDigits,
            thread_id: arg.threadId ?? null,
            description: arg.additionalInfo ?? "",
            state: meta?.individual_info?.state_of_residence ?? "",
            user_id: meta?.user_id,
            type: "consultation",
            referral_category: arg.consultationReferralCategory || "business",
            lawyer_consultation_offer: "default",
            doc_id: arg.consultationDocId ?? null,
          }) as unknown as { type: string },
        );
        return;
      }

      dispatch(setIntentStatus("error"));
    }
  };

// ─── Thunk: recoverCheckoutIntent ────────────────────────────────────────────

export interface RecoverCheckoutIntentArg {
  paymentId: string;
}

interface PaymentStatusResult {
  response: { status?: string; [key: string]: unknown };
}

/**
 * Recover an in-flight checkout journey on page refresh.
 *
 * Branches:
 *   - `successful`        → jump to success slide (SuccessSlide is idempotent).
 *   - `expired`/`failed`  → auto-mint a fresh PI under the same tier; soft
 *                           banner shown in the slide.
 *   - pending/anything    → try localStorage cache. Hit → populate slice. Miss
 *                           → same auto-mint path as expired.
 *
 * No call to `refresh-payment-intent` — see file-header comment.
 */
export const recoverCheckoutIntent =
  (arg: RecoverCheckoutIntentArg) =>
  async (dispatch: ThunkDispatch, getState: ThunkGetState): Promise<void> => {
    dispatch(setIntentStatus("loading"));
    try {
      const { checkDirectPaymentStatus } = getChatSlice();
      const statusAction = checkDirectPaymentStatus({ payment_id: arg.paymentId });
      const statusPromise = dispatch(statusAction) as {
        unwrap: () => Promise<PaymentStatusResult>;
      };
      const statusResult = await statusPromise.unwrap();
      const status = statusResult?.response?.status;

      if (status === "successful") {
        dispatch(setStep("success"));
        return;
      }

      if (status === "expired" || status === "failed") {
        clearPersistedPaymentIntent(arg.paymentId);
        const { purchaseTarget, threadId, entry } = getState().checkoutFlow;
        if (!purchaseTarget) {
          dispatch(setIntentStatus("error"));
          return;
        }
        dispatch(setStaleRecovered(true));
        await (dispatch(
          prepareCheckoutIntent({
            target: purchaseTarget,
            threadId,
            entry: entry ?? undefined,
          }) as unknown as Promise<void>,
        ) as unknown as Promise<void>);
        return;
      }

      // pending / in-progress / anything else → try localStorage cache.
      const cachedPI = readPersistedPaymentIntent<ChargebeePaymentIntent>(arg.paymentId);
      if (cachedPI && cachedPI.id) {
        dispatch(
          setPaymentIntent({
            paymentId: arg.paymentId,
            paymentIntent: cachedPI,
            gateway: readPersistedGateway(arg.paymentId) as GatewayKey | null,
            intentStatus: "ready",
          }),
        );
        return;
      }

      // Cache miss → auto-mint, same UX as expired/failed.
      const { purchaseTarget, threadId, entry } = getState().checkoutFlow;
      if (!purchaseTarget) {
        dispatch(setIntentStatus("error"));
        return;
      }
      dispatch(setStaleRecovered(true));
      await (dispatch(
        prepareCheckoutIntent({
          target: purchaseTarget,
          threadId,
          entry: entry ?? undefined,
        }) as unknown as Promise<void>,
      ) as unknown as Promise<void>);
    } catch {
      dispatch(setIntentStatus("error"));
    }
  };

// ─── Thunk: switchTier ───────────────────────────────────────────────────────

/**
 * Tier-switch wrapper that clears the previous PI's localStorage entry before
 * the reducer wipes the slice. The reducer cannot perform side effects, so
 * the localStorage clean-up has to live in a thunk.
 */
export const switchTier =
  (tier: CheckoutTier) =>
  (dispatch: ThunkDispatch, getState: ThunkGetState): void => {
    const currentPaymentId = getState().checkoutFlow.paymentId;
    const currentTier = getState().checkoutFlow.selectedTier;
    if (currentTier !== tier && currentPaymentId) {
      clearPersistedPaymentIntent(currentPaymentId);
    }
    dispatch(setSelectedTier(tier));
  };

// ─── Thunk: closeAndCleanup ──────────────────────────────────────────────────

/**
 * Close wrapper that clears the active PI's localStorage entry before the
 * reducer resets the slice. CheckoutFlowModal's onClose handler should
 * dispatch this instead of bare `close()`.
 */
export const closeAndCleanup =
  () =>
  (dispatch: ThunkDispatch, getState: ThunkGetState): void => {
    const currentPaymentId = getState().checkoutFlow.paymentId;
    if (currentPaymentId) {
      clearPersistedPaymentIntent(currentPaymentId);
    }
    // The persisted shelf description dies with the flow — a later, unrelated
    // checkout must not inherit it (consultation-contract-rev3).
    clearConsultationDescription();
    dispatch(close());
  };

// ─── Action type strings for matchers that need them (tests) ────────────────

export const openAtType = openAt.type;

export default checkoutFlowSlice.reducer;
